import { useEffect, useRef, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import toast from 'react-hot-toast';
import {
  db,
  replyToEntityRequest,
  resolveEntityRequest,
  type CalibreRequestPayload,
  type EntityRequest,
  type EntityRequestMessage,
  type LookupRequestPayload,
} from '../../services/firebase';
import { Modal } from '../common/Modal';
import { CustomCalibreModal } from '../CuratedWatches/CustomCalibreModal';
import { ApproveLookupValueModal } from '../BrandPortalRequests/ApproveLookupValueModal';
import { humanizeLookupCollection } from './RequestNewValueModal';
import { StatusBadge, relativeTime, requestTitle } from './MyRequestsPanel';

/**
 * RequestDetailPanel — Brand Portal Phase 2.5 (chunk iii).
 *
 * Opened from `MyRequestsPanel` inside a `<Modal>`. Shows the request summary,
 * the resolution outcome (approved entity / reject reason / needs-info prompt),
 * a live message thread, and a reply composer.
 *
 * The `request` prop is owned by the parent's live list snapshot, so status
 * transitions (admin approves/rejects) flow in without a second doc listener
 * here. Messages have their own `onSnapshot` on the `messages` subcollection.
 * Replies go through the `replyToEntityRequest` callable (callable-only writes);
 * the new message appears via the listener, not optimistic local state.
 */

const REPLY_MAX = 1000;

interface Props {
  request: EntityRequest;
  /** Brand display label, or null when the user only spans one brand. */
  brandLabel: string | null;
  onClose: () => void;
  /**
   * Render from the admin's perspective (chunk iv). Flips the message thread so
   * the admin's own replies sit on the right ("You") and the brand owner's sit on
   * the left ("Brand owner") — the mirror of the brand-owner view. Labels only;
   * the reply path (`replyToEntityRequest`) is already role-gated server-side.
   */
  adminMode?: boolean;
  /**
   * Resolve a uid to a human label (email / display name) for the resolution
   * read-out. Supplied by the admin inbox; falls back to the raw uid.
   */
  resolveUserLabel?: (uid: string) => string;
}

function absoluteTime(ts?: { toDate: () => Date }): string {
  if (!ts || typeof ts.toDate !== 'function') return '';
  try {
    return ts.toDate().toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

/** Labelled summary row; renders nothing when the value is empty. */
function Field({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div>
      <dt className='text-xs text-gray-500'>{label}</dt>
      <dd className='text-sm text-gray-900 whitespace-pre-line'>{value}</dd>
    </div>
  );
}

export function RequestDetailPanel({
  request,
  brandLabel,
  onClose,
  adminMode = false,
  resolveUserLabel,
}: Props) {
  const [messages, setMessages] = useState<EntityRequestMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(true);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  // Admin action-bar state (chunk v).
  const [actionInFlight, setActionInFlight] = useState(false);
  const [calibreModalOpen, setCalibreModalOpen] = useState(false);
  const [lookupModalOpen, setLookupModalOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [needsInfoOpen, setNeedsInfoOpen] = useState(false);
  const [needsInfoMsg, setNeedsInfoMsg] = useState('');
  // Tracks whether the calibre/lookup editor closed via a successful save, so a
  // plain close (cancel mid-approve) leaves the request pending.
  const approveSavedRef = useRef(false);

  const threadEndRef = useRef<HTMLDivElement | null>(null);

  // Live message thread.
  useEffect(() => {
    setMessagesLoading(true);
    const q = query(
      collection(db, 'entityRequests', request.id, 'messages'),
      orderBy('createdAt', 'asc'),
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setMessages(
          snapshot.docs.map(
            (d) => ({ id: d.id, ...d.data() }) as EntityRequestMessage,
          ),
        );
        setMessagesLoading(false);
      },
      () => setMessagesLoading(false),
    );
    return unsubscribe;
  }, [request.id]);

  // Keep the latest message in view as the thread grows.
  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ block: 'nearest' });
  }, [messages.length]);

  const isResolved =
    request.status === 'approved' || request.status === 'rejected';
  const canSend = body.trim() !== '' && !sending;

  const handleSend = async () => {
    const trimmed = body.trim();
    if (!trimmed || sending) return;
    setSending(true);
    try {
      await replyToEntityRequest({ requestId: request.id, body: trimmed });
      setBody(''); // the new message arrives via the snapshot listener
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to send your reply';
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  const copyEntityId = async () => {
    if (!request.resolvedEntityId) return;
    try {
      await navigator.clipboard.writeText(request.resolvedEntityId);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Could not copy — copy it manually');
    }
  };

  // ── Admin actions (chunk v) ─────────────────────────────────────────────────
  // The client creates the entity (calibre/lookup) first, then records the
  // resolution via `resolveEntityRequest`. Status flips arrive via the parent's
  // live snapshot, so there's no manual refresh.

  const isOpenStatus =
    request.status === 'pending' || request.status === 'needs_info';

  const handleApproveClick = () => {
    approveSavedRef.current = false;
    if (request.type === 'calibre') setCalibreModalOpen(true);
    else setLookupModalOpen(true);
  };

  const finishApprove = async (resolvedEntityId: string) => {
    setActionInFlight(true);
    try {
      await resolveEntityRequest({
        requestId: request.id,
        decision: 'approve',
        resolvedEntityId,
      });
      toast.success(
        request.type === 'calibre'
          ? 'Calibre created and request approved'
          : 'Lookup value created and request approved',
      );
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to record the approval';
      // The entity was created but the resolution didn't stick — surface it so
      // the admin can retry (a re-approve will reuse/merge the existing entity).
      toast.error(`${message} — entity created but request still pending.`);
    } finally {
      setActionInFlight(false);
    }
  };

  const handleEntitySaved = (savedId: string) => {
    approveSavedRef.current = true;
    setCalibreModalOpen(false);
    setLookupModalOpen(false);
    void finishApprove(savedId);
  };

  const handleApproveEditorClose = () => {
    setCalibreModalOpen(false);
    setLookupModalOpen(false);
    if (!approveSavedRef.current) {
      toast('Approval cancelled — request still pending.');
    }
  };

  const handleRejectSubmit = async () => {
    const reason = rejectReason.trim();
    if (!reason || actionInFlight) return;
    setActionInFlight(true);
    try {
      await resolveEntityRequest({
        requestId: request.id,
        decision: 'reject',
        rejectReason: reason,
      });
      toast.success('Request rejected');
      setRejectOpen(false);
      setRejectReason('');
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to reject the request',
      );
    } finally {
      setActionInFlight(false);
    }
  };

  const handleNeedsInfoSubmit = async () => {
    const message = needsInfoMsg.trim();
    if (!message || actionInFlight) return;
    setActionInFlight(true);
    try {
      await resolveEntityRequest({
        requestId: request.id,
        decision: 'needs_info',
        message,
      });
      toast.success('Request marked as needs more info — brand owner notified');
      setNeedsInfoOpen(false);
      setNeedsInfoMsg('');
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to update the request',
      );
    } finally {
      setActionInFlight(false);
    }
  };

  // ── Summary fields ──────────────────────────────────────────────────────────

  const typeLabel =
    request.type === 'calibre'
      ? 'Calibre'
      : `Lookup · ${humanizeLookupCollection(
          (request.payload as { collection: string }).collection,
        )}`;

  const calibre =
    request.type === 'calibre'
      ? (request.payload as {
          manufacturer?: string;
          movementType?: string;
          notes?: string;
        })
      : null;
  const lookup =
    request.type === 'lookup'
      ? (request.payload as { collection: string; notes?: string })
      : null;

  // Context banner for the calibre create editor: manufacturer (free text — not
  // a clean form field) and the brand owner's notes, surfaced for the admin.
  const calibreApproveNote = (() => {
    if (!calibre) return undefined;
    const bits: string[] = [];
    if (calibre.manufacturer) bits.push(`Requested manufacturer: ${calibre.manufacturer}`);
    if (calibre.notes) bits.push(`Brand owner notes: ${calibre.notes}`);
    return bits.length ? bits.join('\n') : undefined;
  })();

  // Resolution read-out for admins: who resolved it and when.
  const resolutionMeta = (() => {
    const who = request.resolvedBy
      ? (resolveUserLabel?.(request.resolvedBy) ?? request.resolvedBy)
      : null;
    const when = absoluteTime(request.resolvedAt);
    const parts: string[] = [];
    if (who) parts.push(`by ${who}`);
    if (when) parts.push(when);
    return parts.length ? `Resolved ${parts.join(' · ')}` : null;
  })();

  return (
    <div className='space-y-5'>
      {/* Summary */}
      <div className='space-y-3'>
        <div className='flex items-center gap-3'>
          <StatusBadge status={request.status} size='lg' />
          <span className='text-xs text-gray-400'>{typeLabel}</span>
        </div>

        <div>
          <h3 className='text-lg font-semibold text-gray-900'>
            {requestTitle(request)}
          </h3>
        </div>

        <dl className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
          {brandLabel && <Field label='Brand' value={brandLabel} />}
          <div>
            <dt className='text-xs text-gray-500'>Submitted</dt>
            <dd className='text-sm text-gray-900' title={absoluteTime(request.createdAt)}>
              {relativeTime(request.createdAt)}
            </dd>
          </div>
          {calibre && <Field label='Manufacturer' value={calibre.manufacturer} />}
          {calibre && <Field label='Movement type' value={calibre.movementType} />}
          {lookup && (
            <Field
              label='Collection'
              value={humanizeLookupCollection(lookup.collection)}
            />
          )}
          <Field label='Notes' value={calibre?.notes ?? lookup?.notes} />
        </dl>

        {request.context?.sourceReferenceId && (
          <p className='text-xs text-gray-400'>
            Filed from a reference edit
            {request.context.sourceField
              ? ` (${request.context.sourceField})`
              : ''}{' '}
            · ref{' '}
            <span className='font-mono text-gray-500'>
              {request.context.sourceReferenceId}
            </span>
          </p>
        )}
      </div>

      {/* Resolution callouts */}
      {request.status === 'approved' && (
        <div className='rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-900'>
          <p className='font-medium'>Approved — added to the catalogue.</p>
          {request.resolvedEntityId && (
            <div className='mt-2 flex items-center gap-2'>
              <span className='text-xs text-green-800'>Entity id:</span>
              <code className='text-xs bg-white border border-green-200 rounded px-1.5 py-0.5 text-green-900'>
                {request.resolvedEntityId}
              </code>
              <button
                type='button'
                onClick={copyEntityId}
                className='text-xs font-medium text-green-700 hover:text-green-800 underline'
              >
                Copy
              </button>
            </div>
          )}
          {adminMode && resolutionMeta && (
            <p className='mt-2 text-xs text-green-700'>{resolutionMeta}</p>
          )}
        </div>
      )}

      {request.status === 'rejected' && (
        <div className='rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-900'>
          <p className='font-medium'>Rejected</p>
          {request.rejectReason && (
            <p className='mt-1 whitespace-pre-line'>{request.rejectReason}</p>
          )}
          {adminMode && resolutionMeta && (
            <p className='mt-2 text-xs text-red-700'>{resolutionMeta}</p>
          )}
        </div>
      )}

      {request.status === 'needs_info' && (
        <div className='rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900'>
          {adminMode ? (
            <>
              <p className='font-medium'>Waiting on the brand owner</p>
              <p className='mt-1'>
                Brand owner needs to reply with more info before this can be
                approved.
              </p>
            </>
          ) : (
            <>
              <p className='font-medium'>Admin needs more info</p>
              <p className='mt-1'>Reply below so they can continue the review.</p>
            </>
          )}
        </div>
      )}

      {/* Admin action bar — only while the request is open (chunk v). */}
      {adminMode && isOpenStatus && (
        <div className='flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2'>
          <span className='text-xs font-medium text-gray-500 mr-1'>
            Decision:
          </span>
          <button
            type='button'
            onClick={handleApproveClick}
            disabled={actionInFlight}
            className='px-3 py-1.5 text-sm font-medium rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50'
          >
            Approve
          </button>
          <button
            type='button'
            onClick={() => setNeedsInfoOpen(true)}
            disabled={actionInFlight}
            className='px-3 py-1.5 text-sm font-medium rounded bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50'
          >
            Needs more info
          </button>
          <button
            type='button'
            onClick={() => setRejectOpen(true)}
            disabled={actionInFlight}
            className='px-3 py-1.5 text-sm font-medium rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50'
          >
            Reject
          </button>
        </div>
      )}

      {/* Message thread */}
      <div>
        <h4 className='text-sm font-semibold text-gray-900 mb-2'>Messages</h4>
        <div className='max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3'>
          {messagesLoading ? (
            <p className='text-center text-sm text-gray-400 py-4'>
              Loading messages…
            </p>
          ) : messages.length === 0 ? (
            <p className='text-center text-sm text-gray-400 py-4'>
              No messages yet.
            </p>
          ) : (
            messages.map((m) => {
              // Admins see their own ('admin') replies on the right; brand
              // owners see theirs ('brand'). Same thread, mirrored.
              const mine = adminMode
                ? m.authorRole === 'admin'
                : m.authorRole === 'brand';
              const otherLabel = adminMode ? 'Brand owner' : 'Admin';
              return (
                <div
                  key={m.id}
                  className={`flex flex-col ${mine ? 'items-end' : 'items-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                      mine
                        ? 'bg-brand-600 text-white'
                        : 'bg-white border border-gray-200 text-gray-900'
                    }`}
                  >
                    <p className='whitespace-pre-line break-words'>{m.body}</p>
                  </div>
                  <span className='mt-0.5 text-[11px] text-gray-400'>
                    {mine ? 'You' : otherLabel} · {relativeTime(m.createdAt)}
                  </span>
                </div>
              );
            })
          )}
          <div ref={threadEndRef} />
        </div>
      </div>

      {/* Composer — hidden once resolved (nothing to reply to) */}
      {isResolved ? (
        <p className='text-xs text-gray-400 text-center'>
          This request is resolved. Start a new request if you need another value.
        </p>
      ) : (
        <div className='space-y-2'>
          {request.status === 'needs_info' && (
            <p className='text-xs font-medium text-amber-700'>
              {adminMode
                ? 'Waiting on the brand owner to respond.'
                : 'Admin is waiting for more info.'}
            </p>
          )}
          <textarea
            value={body}
            maxLength={REPLY_MAX}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            placeholder='Write a reply…'
            className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
          />
          <div className='flex items-center justify-between'>
            <span className='text-xs text-gray-400'>
              {body.length > 800 ? `${body.length}/${REPLY_MAX}` : ''}
            </span>
            <div className='flex items-center gap-3'>
              <button
                type='button'
                onClick={onClose}
                className='px-4 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50'
              >
                Close
              </button>
              <button
                type='button'
                onClick={handleSend}
                disabled={!canSend}
                className='px-4 py-2 text-sm bg-brand-600 text-white rounded hover:bg-brand-700 disabled:opacity-50'
              >
                {sending ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Approve flow editors ─────────────────────────────────────────────── */}
      {adminMode && calibreModalOpen && request.type === 'calibre' && (
        <CustomCalibreModal
          isOpen
          initialMode='create'
          initialCreateValues={{
            name: (request.payload as CalibreRequestPayload).name,
            movementType: (request.payload as CalibreRequestPayload).movementType,
          }}
          requestNote={calibreApproveNote}
          onSaved={handleEntitySaved}
          onClose={handleApproveEditorClose}
        />
      )}

      {adminMode && lookupModalOpen && request.type === 'lookup' && (
        <ApproveLookupValueModal
          isOpen
          collection={(request.payload as LookupRequestPayload).collection}
          initialValue={(request.payload as LookupRequestPayload).value}
          note={(request.payload as LookupRequestPayload).notes}
          onCreated={handleEntitySaved}
          onClose={handleApproveEditorClose}
        />
      )}

      {/* ── Reject confirmation ──────────────────────────────────────────────── */}
      {adminMode && (
        <Modal
          isOpen={rejectOpen}
          onClose={() => {
            if (actionInFlight) return;
            setRejectOpen(false);
            setRejectReason('');
          }}
          title='Reject request'
          size='sm'
        >
          <div className='space-y-4'>
            <p className='text-sm text-gray-600'>
              Reject this request? Please provide a reason — the brand owner will
              see it.
            </p>
            <textarea
              value={rejectReason}
              maxLength={1000}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
              placeholder='Reason for rejection…'
              className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
            />
            <div className='flex justify-end gap-3'>
              <button
                type='button'
                onClick={() => {
                  setRejectOpen(false);
                  setRejectReason('');
                }}
                disabled={actionInFlight}
                className='px-4 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50'
              >
                Cancel
              </button>
              <button
                type='button'
                onClick={handleRejectSubmit}
                disabled={actionInFlight || !rejectReason.trim()}
                className='px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50'
              >
                {actionInFlight ? 'Rejecting…' : 'Reject request'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Needs more info ──────────────────────────────────────────────────── */}
      {adminMode && (
        <Modal
          isOpen={needsInfoOpen}
          onClose={() => {
            if (actionInFlight) return;
            setNeedsInfoOpen(false);
            setNeedsInfoMsg('');
          }}
          title='Ask the brand owner for more info'
          size='sm'
        >
          <div className='space-y-4'>
            <p className='text-sm text-gray-600'>
              Send a message to the brand owner. The request moves to “needs
              info” until they reply.
            </p>
            <textarea
              value={needsInfoMsg}
              maxLength={1000}
              onChange={(e) => setNeedsInfoMsg(e.target.value)}
              rows={4}
              placeholder='What do you need from the brand owner?'
              className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
            />
            <div className='flex justify-end gap-3'>
              <button
                type='button'
                onClick={() => {
                  setNeedsInfoOpen(false);
                  setNeedsInfoMsg('');
                }}
                disabled={actionInFlight}
                className='px-4 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50'
              >
                Cancel
              </button>
              <button
                type='button'
                onClick={handleNeedsInfoSubmit}
                disabled={actionInFlight || !needsInfoMsg.trim()}
                className='px-4 py-2 text-sm bg-amber-500 text-white rounded hover:bg-amber-600 disabled:opacity-50'
              >
                {actionInFlight ? 'Sending…' : 'Send & mark needs info'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
